import React, { useRef, useState, useEffect } from "react";
import { Heading } from "components/components/atoms";
import styled from "styled-components";
import { isValidTitle } from "utils/validateTitle";
import cx from "classnames";
import useNewColorSchemaOptions from "utils/hooks/useNewColorSchemaOptions";
import useBreakpoint from "utils/hooks/useBreakpoint";

const SummaryCopy = styled.div`
  ${({ showNewSchemaColor, newSchemaTextColorHexaCode }) =>
    showNewSchemaColor && `color: ${newSchemaTextColorHexaCode};`}
`;
const MoreSummary = styled.span`
  ${({ showNewSchemaColor, newSchemaTextColorHexaCode }) =>
    showNewSchemaColor && `color: ${newSchemaTextColorHexaCode};`}
`;

const Img = styled.img`
  ${({ isHorizontal }) =>
    isHorizontal ? "max-height: 3.5rem;" : "max-height: 7rem;"}
`;

const CommunityProductOverviewSummary = ({ productOverviewView }) => {
  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["sm", "xs"].includes(breakpoint);
  const isTablet = ["md"].includes(breakpoint);
  const isIPad = ["lg"].includes(breakpoint);
  const max_caracter_count = isMobile
    ? 250
    : isTablet
    ? 370
    : isIPad
    ? 500
    : 680;

  const {
    newSchemaTextColorHexaCode,
    showNewSchemaColor,
  } = useNewColorSchemaOptions();

  const textAlignment = productOverviewView.textAlignment;

  const withLogo = productOverviewView.image && productOverviewView.image.src;
  const [isHorizontal, setIsHorizontal] = useState(false);
  const imgRef = useRef();
  useEffect(() => {
    if (imgRef.current) {
      const imgHeight = imgRef.current.naturalHeight;
      const imgWidth = imgRef.current.naturalWidth;
      setIsHorizontal(imgWidth > imgHeight);
    }
  }, [imgRef]);

  //render Summary section
  const [openMore, setOpenMore] = useState(false);
  const getMoreSpan = (textContent, htmlContent) => {
    const componentClassName = "product-summary-component";

    const hyperlinkStyle = `
    .${componentClassName} a {
      font-weight: bold !important;
      text-decoration: underline !important;
    }
  `;

    const truncateHTMLContent = (html, maxLength) => {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;

      let textLength = 0;
      let result = "";

      function traverseNodes(node) {
        if (textLength >= maxLength) return;

        const childNodes = node.childNodes;
        for (let i = 0; i < childNodes.length; i++) {
          const child = childNodes[i];

          if (child.nodeType === Node.TEXT_NODE) {
            const text = child.textContent;
            const remainingLength = maxLength - textLength;

            if (textLength + text.length <= maxLength) {
              result += text;
              textLength += text.length;
            } else {
              result += text.substring(0, remainingLength) + "...";
              textLength = maxLength;
              return;
            }
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            const tagName = child.tagName.toLowerCase();
            const openTag = `<${tagName}`;
            const attributes = Array.from(child.attributes)
              .map((attr) => ` ${attr.name}="${attr.value}"`)
              .join("");

            const closeOpenTag = `>`;

            result += openTag + attributes + closeOpenTag;

            traverseNodes(child);

            result += `</${tagName}>`;

            if (textLength >= maxLength) return;
          }
        }
      }

      traverseNodes(tempDiv);
      return `<style>${hyperlinkStyle}</style>${result}`;
    };

    const truncatedHTML =
      typeof window !== "undefined"
        ? truncateHTMLContent(htmlContent, max_caracter_count - 3)
        : textContent.substring(0, max_caracter_count - 3) + "...";

    return (
      <div className={componentClassName}>
        <span dangerouslySetInnerHTML={{ __html: truncatedHTML }} />
        <MoreSummary
          className={cx("font-graphie font-bold cursor-pointer", {
            "text-mattamy-blue ": !showNewSchemaColor,
          })}
          aria-label={openMore ? "Show less" : "Show more"}
          aria-expanded={!openMore}
          as={"button"}
          tabIndex="0"
          onClick={() => {
            setOpenMore(true);
          }}
          onKeyUp={(e) => {
            if (e.key === "Enter") setOpenMore(true);
          }}
        >
          {"More+"}
        </MoreSummary>
      </div>
    );
  };

  const getLessSpan = (htmlContent) => {
    const componentClassName = "product-summary-component";

    const styledHtmlContent = `
    <style>
      .${componentClassName} a {
        font-weight: bold !important;
        text-decoration: underline !important;
      }
    </style>
    ${htmlContent}
  `;

    return (
      <div className={componentClassName}>
        <span dangerouslySetInnerHTML={{ __html: styledHtmlContent }} />
        <MoreSummary
          className={cx("font-graphie font-bold cursor-pointer ml-1", {
            "text-mattamy-blue ": !showNewSchemaColor,
          })}
          showNewSchemaColor={showNewSchemaColor}
          newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
          aria-label={openMore ? "Show less" : "Show more"}
          aria-expanded={openMore}
          as={"button"}
          tabIndex="0"
          onClick={() => {
            setOpenMore(false);
          }}
          onKeyUp={(e) => {
            if (e.key === "Enter") setOpenMore(false);
          }}
        >
          {" Less-"}
        </MoreSummary>
      </div>
    );
  };

  const renderSummary = () => {
    const productCopy = productOverviewView.copy;
    const spanHTML =
      typeof window !== "undefined" && document
        ? document.createElement("span")
        : null;
    if (spanHTML) spanHTML.innerHTML = productCopy;
    const copyText = spanHTML && (spanHTML.textContent || spanHTML.innerText);

    const componentClassName = "product-summary-component";

    const shortContentWithStyledLinks = `
    <style>
      .${componentClassName} a {
        font-weight: bold !important;
        text-decoration: underline !important;
      }
    </style>
    ${productCopy}
  `;

    return (
      <>
        {withLogo && (
          <div className="self-center">
            <Img
              className="mb-8"
              ref={imgRef}
              isHorizontal={isHorizontal}
              src={productOverviewView.image.src}
              alt={productOverviewView.image.alt}
            />
          </div>
        )}
        {isValidTitle(productOverviewView.headline) && (
          <Heading
            as="h2"
            color={showNewSchemaColor ? newSchemaTextColorHexaCode : "blue"}
            className={cx("text-2xl md:text-4xl leading-10", {
              "text-left": textAlignment === "left",
              "text-center": textAlignment === "center",
              "text-right": textAlignment === "right",
            })}
          >
            {productOverviewView.headline}
          </Heading>
        )}
        <SummaryCopy
          className={cx(
            "text-normal mb-10-mattamy font-graphie w-full font-graphie leading-7",
            {
              "text-mattamy-blue": !showNewSchemaColor,
              "text-left": textAlignment === "left",
              "text-center": textAlignment === "center",
              "text-right": textAlignment === "right",
            }
          )}
          showNewSchemaColor={showNewSchemaColor}
          newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
        >
          {copyText && copyText.length > max_caracter_count && !openMore ? (
            getMoreSpan(copyText, productCopy)
          ) : (
            <p></p>
          )}
          {copyText && copyText.length > max_caracter_count && openMore ? (
            getLessSpan(copyText, productCopy)
          ) : (
            <p></p>
          )}
          {copyText && copyText.length <= max_caracter_count ? (
            <div
              className={componentClassName}
              dangerouslySetInnerHTML={{ __html: shortContentWithStyledLinks }}
            />
          ) : (
            <p></p>
          )}
        </SummaryCopy>
      </>
    );
  };

  return <>{renderSummary()}</>;
};

export default CommunityProductOverviewSummary;
